import React, { memo, useCallback, useContext, useMemo } from 'react';
import { IColumnDef } from '../../../../../../Types/ColumnDefType';
import { IUIUrval } from '../../../../../../Types/UrvalTypes';
import {
  findValue,
  getFormattedColumnValue,
} from '../ObjectTable/ObjectTableRow';
import EDTypography from '../../../../../../BaseComponents/Typography/EDTypography';
import { EDCard } from '../../../../../../BaseComponents/Card/EDCard';
import { EDCardContent } from '../../../../../../BaseComponents/Card/EDCardContent';
import { EDEmotionCardHeader } from '../../../../../../BaseComponents/Card/EDEmotionCardHeader';
import EDCheckbox from '../../../../../../BaseComponents/Checkboxes/EDCheckbox';
import { IClientBusinessModel } from '../../../../../../Types/ClientModelTypes';
import BusinessValuePair from './BusinessValuePair';
import FunctionMenu from '../../FunctionMenu/FunctionMenu/FunctionMenu';
import {
  IBusinessListIconDef,
  IBusinessColorConfig,
} from '../../../Const/BusinessConfigs';
import { useDefaultFunctionMenu } from '../../FunctionMenu/DefaultFunctionMenu/useDefaultFunctionMenu';
import FunctionItem from '../../FunctionMenu/FunctionItem/FunctionItem';
import { useItemFunctionMenuItemsType } from '../../../../../../Utils/ObjektVyUtils';
import { useThrowIfPropHasChanged } from '../../../../../../Hooks/useThrowIfPropHasChanged';
import BusinessDetailContext from '../../BusinessDetailContext/BusinessDetailContext';
import { TSelectedDetail } from '../../../../../../Types/MasterDetailTypes';
import EDBox from '../../../../../../BaseComponents/EDBox/EDBox';
import LegendArea from '../../../../../../Components/Legend/LegendArea';
import { ModelContext } from '../../../../../../Model/ModelContext/ModelContext';
import { safeGetAttributeInfo } from '../../../../../../Model/avenyModelUtils';
import { ICollectionDataType } from '../Queries/BusinessCollectionQuery';
import {
  getMasked,
  getMaskedFlagFromCurrentValues,
  MASKED_VALUE_TEXT,
} from '../../../Hooks/useAttributeValues';
import { useIsTouchDevice } from '../../../../../../Components/BreakpointContext/BreakpointContextUtils';
interface IProps {
  columns: readonly IColumnDef[];
  item: ICollectionDataType;
  nextId: string | null;
  prevId: string | null;
  urval: IUIUrval | null;
  collection: IClientBusinessModel;
  associationName?: string;
  selectDetail?: (id: TSelectedDetail) => void;
  selected: boolean;
  toggleSelected: (id: string) => void;
  colors: IBusinessColorConfig[];
  useFunktionsMenyItems?: useItemFunctionMenuItemsType;
  // Temp until implementation is decided upon
  showCheckbox?: boolean;
  businessIcons?: IBusinessListIconDef[];
  disabled?: boolean;
  isMiniCard?: boolean;
  isScrolling: boolean;
  hideDefaultFunktionsMenyItems?: boolean;
}

/**
 *  Komponent som ritar ut key-value pairs för alla kolumner i den ordning de ligger i columns-listan
 */
const BusinessDataGridItem: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const { selectDetail, toggleSelected: toggleSelectedProp } = props;
  const { avenyCollectionMap } = useContext(ModelContext);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (selectDetail) {
        selectDetail({
          id: props.item.id,
          data: props.item,
          nextId: props.nextId,
          prevId: props.prevId,
        });
      }
    },
    [props.item, props.nextId, props.prevId, selectDetail]
  );
  const toggleSelected = useCallback(() => {
    toggleSelectedProp(props.item.id);
  }, [toggleSelectedProp, props.item.id]);
  const colors = useMemo(
    () => props.colors.filter(post => post.condition(props.item)),
    [props.colors, props.item]
  );
  const isTouchDevice = useIsTouchDevice();

  useThrowIfPropHasChanged(
    'Hook useFunktionsMenyItems',
    props.useFunktionsMenyItems
  );

  const defaultFuntionMenyItems = useDefaultFunctionMenu({
    id: props.item.id,
    collection: props.collection,
    associationName: props.associationName,
    item: props.item,
  });
  const funktionsMenyItems = props.useFunktionsMenyItems
    ? [
        ...(props.hideDefaultFunktionsMenyItems ? [] : defaultFuntionMenyItems),
        ...props.useFunktionsMenyItems({
          id: props.item.id,
          collection: props.collection,
          associationName: props.associationName,
          item: props.item,
        }),
      ]
    : props.hideDefaultFunktionsMenyItems
    ? []
    : defaultFuntionMenyItems;

  const visibleFunctionMenyItems = funktionsMenyItems.filter(
    item => !item.hidden
  );

  return (
    <EDBox sx={{ padding: 0.5 }}>
      <BusinessDetailContext.Provider value={{ data: props.item }}>
        <EDCard
          onClick={
            !props.disabled && !props.isScrolling && props.selectDetail
              ? handleClick
              : undefined
          }
          enableRipple
          selected={props.selected}
        >
          <EDBox sx={{ display: 'flex' }}>
            <EDBox sx={{ flexBasis: '8px' }}>
              <LegendArea
                legendData={colors}
                disableTooltip={props.isScrolling}
              />
            </EDBox>
            <EDBox sx={{ flexGrow: 1 }}>
              <EDEmotionCardHeader
                minicard={props.isMiniCard}
                title={
                  props.showCheckbox ? (
                    <EDCheckbox
                      checked={props.selected}
                      onCheck={toggleSelected}
                      checkboxSpacing={{ padding: 8, marginBottom: -8 }}
                      noClickPropagation
                    />
                  ) : undefined
                }
                subheader={
                  <EDBox
                    component="span"
                    sx={{
                      '@media (hover: hover)': {
                        '.MuiCardActionArea-root:hover &': {
                          borderBottom: theme =>
                            `2px solid ${theme.palette.primary.light}`,
                          marginBottom: -2,
                        },
                      },
                    }}
                  >
                    <EDTypography variant="subtitle2">
                      {props.columns
                        .filter(col => col.header)
                        .map(col => {
                          const attrInfo = safeGetAttributeInfo(
                            props.collection,
                            col.attributeDef,
                            avenyCollectionMap
                          );
                          const maskedFlag = getMaskedFlagFromCurrentValues(
                            props.item,
                            col.attributeDef
                          );
                          const masked =
                            attrInfo.isOk() &&
                            getMasked(attrInfo.value.attribute, maskedFlag);
                          if (masked) {
                            return MASKED_VALUE_TEXT;
                          }
                          return getFormattedColumnValue(
                            avenyCollectionMap,
                            props.item,
                            col,
                            props.collection
                          );
                        })
                        .join(' - ')}
                    </EDTypography>
                  </EDBox>
                }
                action={
                  <EDBox sx={{ height: '28px' }}>
                    {
                      <>
                        {props.businessIcons &&
                          props.businessIcons.map(icon =>
                            icon.componentFn({
                              id: props.item.id,
                              data: props.item,
                              disabled: props.disabled,
                            })
                          )}
                        <FunctionMenu
                          id={props.item.id}
                          collection={props.collection}
                          onlyOnHover={!isTouchDevice}
                          disabled={props.disabled}
                          padding="dense"
                          margin="none"
                        >
                          {visibleFunctionMenyItems.map(funktionsMenyItem => (
                            <FunctionItem
                              key={funktionsMenyItem.id}
                              onClick={funktionsMenyItem.onClick}
                              disabled={funktionsMenyItem.disabled}
                              disabledReason={funktionsMenyItem.disabledReason}
                              iconType={funktionsMenyItem.iconType}
                              listItemIcon
                              confirmButtonCaption={
                                funktionsMenyItem.confirmButtonCaption
                              }
                              confirmDialogCaption={
                                funktionsMenyItem.confirmDialogCaption
                              }
                              confirmDialogMessage={
                                funktionsMenyItem.confirmDialogMessage
                              }
                            >
                              {funktionsMenyItem.caption}
                            </FunctionItem>
                          ))}
                        </FunctionMenu>
                      </>
                    }
                  </EDBox>
                }
              />
              <EDCardContent miniCard={props.isMiniCard}>
                {props.columns
                  .filter(col => {
                    const value =
                      findValue(props.item, col.attributeDef.split('.')) ?? '';
                    return !col.header && !(value == '' && props.isMiniCard);
                  })
                  .map((column, index) => {
                    const maskedFlag = getMaskedFlagFromCurrentValues(
                      props.item,
                      column.attributeDef
                    );
                    const attrInfo = safeGetAttributeInfo(
                      props.collection,
                      column.attributeDef,
                      avenyCollectionMap
                    );
                    const masked =
                      attrInfo.isOk() &&
                      getMasked(attrInfo.value.attribute, maskedFlag);
                    return (
                      <BusinessValuePair
                        key={index}
                        column={column}
                        item={props.item}
                        collection={props.collection}
                        isMiniCard={props.isMiniCard}
                        masked={masked}
                      />
                    );
                  })}
              </EDCardContent>
            </EDBox>
          </EDBox>
        </EDCard>
      </BusinessDetailContext.Provider>
    </EDBox>
  );
};
export default memo(BusinessDataGridItem);
